<template>
  <b-form id="new-meeting-participant-form" @submit.prevent="submitForm">
    <b-form-group>
      <template v-slot:label>{{ $t("fullName") }} <span class="text-danger">*</span></template>
      <v-select label="label" class="vs-normalizer"
                :clearable="false"
                :filterable="false"
                :options="typeaheadOptions"
                :clearSearchOnBlur="() => false"
                :clearSearchOnSelect="false"
                @search="doSearchDebounced" @option:selecting="onSelecting">
        <template v-slot:search="{attributes, events}">
          <input id="input-name" v-bind="attributes" v-on="events" v-model="name"
                 class="form-control" required minlength="2" maxlength="100"/>
        </template>
      </v-select>
    </b-form-group>
    <b-form-group>
      <template v-slot:label>Email <span class="text-danger">*</span></template>
      <b-form-input type="email" v-model="email" required maxlength="100"></b-form-input>
    </b-form-group>
    <b-form-group>
      <template v-slot:label>{{ $t("phoneNumber") }}</template>
      <b-form-input type="tel" v-model="phone" :pattern="phonePattern" maxlength="50"
                    @invalid="$event.target.setCustomValidity($t('invalidPhoneFormat'))"
                    @input.native="$event.target.setCustomValidity('')" />
    </b-form-group>

    <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>

    <div class="text-right pt-3">
      <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">{{ $t("cancel") }}</b-button>
      <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("save") }}</b-button>
    </div>
  </b-form>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Emit, Prop, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as meetingService from "@/service/meetingService";
  import * as validationUtils from "@/service/validationUtils";
  import * as contactService from "@/service/contactService";
  import * as util from "@/service/util";

  @Component
  export default class NewMeetingParticipantModal extends BaseComponent {

    @Prop({ required: true }) modalId: string;
    @Prop({ required: true }) meetingId: number;

    name = "";
    email = "";
    phone = "";

    doSearchDebounced: Function = null;
    typeaheadOptions = [];

    formErrorMsg = "";
    isSubmitting = false;

    // Emit decorators only
    @Emit("submitted") __submitted() {}


    mounted() {
      this.doSearchDebounced = _.debounce(async (q: string, loading: (p: boolean) => void) => {
        try {
          if (q.length < 2) {
            this.typeaheadOptions = [];
          } else {
            loading(true);
            this.typeaheadOptions = (await contactService.getAllContacts(1, 100, q)).data;

            // Typeahead option label: "name - department"
            for (const contactOption of this.typeaheadOptions) {
              contactOption.label = util.joinStrings([contactOption.name, contactOption.department, contactOption.organization], " - ");
            }
          }

        } catch (e) {
          this.toastError(e);

        } finally {
          loading(false);
        }
      }, 300);
    }


    onSelecting(option) {
      this.name = option.name;
      this.phone = option.phone;
      this.email = option.email;
      util.clearCustomValidity();
    }


    onClickCancel() {
      this.$bvModal.hide(this.modalId);
    }


    async submitForm() {
      this.formErrorMsg = "";

      if (!validationUtils.isEmail(this.email)) {
        this.formErrorMsg = this.$t("invalidEmail").toString();
        return;
      }

      const data = {
        meetingId: this.meetingId,
        name: this.name,
        email: this.email,
        phone: this.phone,
        role: "PARTICIPANT",
        assignedBy: this.userId,
      };

      try {
        this.isSubmitting = true;
        await meetingService.addNewParticipant(data);

        this.__submitted();
        this.toastSuccessDelay(this.$t("addMeetingParticipantSuccessful").toString());
        this.$bvModal.hide(this.modalId);

      } catch (e) {
        this.toastError(e);

      } finally {
        this.isSubmitting = false;
      }
    }

  }
</script>
